import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useStyles } from './styles/ReportTable.styles';
import { gatherColumns } from '@utils/summaries';
import { useEffect, useState } from 'react';
import { ReportTableHeader } from '../ReportTableHeader';
import { styleNumber } from '@utils/styles';
import { colors, typography as typographyTokens } from '@components/design-system/tokens';

import type { TableOptions } from '@/types/table';

interface ReportTableProps {
    reportName: string;
    tableOptions: TableOptions;
    loading: boolean;
    height: number;
}

export const ReportTable = ({ reportName, tableOptions, loading, height }: ReportTableProps) => {
    const classes = useStyles();

    const [sortedData, setSortedData] = useState<any[]>([]);

    useEffect(() => {
        setSortedData([...tableOptions.data]);
    }, [tableOptions.data]);

    const sortTableData = (index: number, direction: 'ascending' | 'descending') => {
        const newData = [...sortedData].sort((rowA: string[] | number[], rowB: string[] | number[]) => {
            const sortMethod = tableOptions.sort[index];
            return sortMethod(rowA[index], rowB[index], direction);
        });
        setSortedData(newData);
    };

    return (
        <Box className={classes.wrapper} sx={{ height, maxHeight: height, display: 'flex', flexDirection: 'column' }}>
            {loading || (tableOptions.data.length !== 0 && sortedData.length === 0) ? (
                <Box className={classes.loadingIndicator}>
                    <CircularProgress sx={{ color: colors.primary[500] }} />
                </Box>
            ) : (
                <TableContainer className={classes.container} sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader aria-label='sticky table'>
                        <TableHead>
                            <TableRow>
                                {tableOptions.headers.map((header: string, index: number) => (
                                    <ReportTableHeader
                                        key={`header - ${reportName} - ${header} - ${index}`}
                                        header={header}
                                        onSort={(direction) => sortTableData(index, direction)}
                                    />
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {tableOptions.data.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={tableOptions.headers.length}
                                        sx={{
                                            textAlign: 'center',
                                            py: 6,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: colors.neutral[500],
                                                fontSize: typographyTokens.fontSize.sm,
                                            }}>
                                            There is no data to show
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {sortedData.map((row: (string | number)[], idx: number) => {
                                const header = tableOptions.headers[idx];

                                return (
                                    <TableRow
                                        key={`row - ${reportName} - ${header} - ${idx}`}
                                        className={classes.tableRow}>
                                        {row.map((item: string | number, idx: number) => {
                                            const styleFunc = tableOptions.style?.[idx];

                                            return (
                                                <TableCell
                                                    key={`${reportName} - ${header} - ${item} - ${idx}`}
                                                    align='left'>
                                                    {styleFunc ? styleFunc(item) : styleNumber(item)}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>

                        <TableFooter className={classes.tableFooter}>
                            <TableRow>
                                {tableOptions.data.length > 0 &&
                                    tableOptions.headers.map((header: string, idx: number) => {
                                        const summaryFunc = tableOptions.summaries[idx];

                                        const columns = gatherColumns(tableOptions.data, idx);

                                        const summary = summaryFunc ? summaryFunc(columns) : '';

                                        return (
                                            <TableCell
                                                key={`${reportName} - ${header} - ${summary}`}
                                                align='left'>
                                                {styleNumber(summary)}
                                            </TableCell>
                                        );
                                    })}
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};
